import type { ReactElement } from "react";
import type { Zuordnung } from "@kundeninfo/api-client";
import { Chip } from "@kundeninfo/ui/me-design";
import { PARTNER_TEXT } from "./texts.js";

/** Die Rollen eines Geschäftspartners als Schlüssel der Kennzeichen-Texte (auch für `kennzeichenFolge`, PF6). */
export type Badge = keyof typeof PARTNER_TEXT.badge;
type Rollen = Pick<Zuordnung, "status" | "supplierStatus">;

/** Kundenrolle (`status`) → Kennzeichen; „Kunde“ steht für ACTIVE (Spec „Kennzeichen, Listen und Filter“). */
const KUNDENROLLE: Record<NonNullable<Zuordnung["status"]>, Badge> =
  { PROSPECT: "PROSPECT", ACTIVE: "CUSTOMER", DORMANT: "DORMANT", FORMER: "FORMER" };
const LIEFERANTENROLLE: Record<NonNullable<Zuordnung["supplierStatus"]>, Badge> =
  { ACTIVE: "SUPPLIER", FORMER: "SUPPLIER_FORMER" };
const EHEMALIG: readonly Badge[] = ["FORMER", "SUPPLIER_FORMER"];

/**
 * Die Rollen EINER Beziehung als Kennzeichen-Texte, Kundenrolle vor Lieferantenrolle. Die Ehemaligen zeigt die Liste
 * nur bei „ehemalige zeigen“ (C9), der Kopf immer. Fehlende Felder (ältere Antworten) gelten als „keine Rolle“.
 */
export function partnerBadges(r: Rollen, mitEhemaligen: boolean): string[] {
  return partnerBadgeKeys(r, mitEhemaligen).map((b) => PARTNER_TEXT.badge[b]);
}

/** Dieselben Rollen als Schlüssel von `PARTNER_TEXT.badge` — für die Kürzel-Chips der Liste (Entscheid 525a). */
export function partnerBadgeKeys(r: Rollen, mitEhemaligen: boolean): Badge[] {
  return [
    r.status == null ? null : KUNDENROLLE[r.status],
    r.supplierStatus == null ? null : LIEFERANTENROLLE[r.supplierStatus],
  ].filter((b): b is Badge => b !== null && (mitEhemaligen || !EHEMALIG.includes(b)));
}

/**
 * Der Satz über die Beziehungen eines Geschäftspartners über „Rollen je Unternehmen“ — „Zugeordnet bei 2 Unternehmen:
 * Meine-Energie GmbH (Kunde und Lieferant), Bitshifters GmbH (Interessent)“. Die Rollen tragen dieselben Wörter wie die
 * Kennzeichen daneben, samt Ehemaligen; bis zur Handbuch-Übergabe vom 02.11.2026 stand hier der Status roh („(ACTIVE)“).
 */
export function relationsSentence(relations: readonly (Rollen & Pick<Zuordnung, "companyName">)[]): string {
  if (relations.length === 0) return PARTNER_TEXT.noRelation;
  return PARTNER_TEXT.relationsLine(relations.map((r) => PARTNER_TEXT.relationEntry(r.companyName, partnerBadges(r, true))));
}

/**
 * Die Rollen einer Beziehung als Kennzeichen (`me-chip` ohne `entfernbar`: Fläche, kein Rahmen — Hausregel 27.10.)
 * für Kopf, Kopfkachel und „Rollen je Unternehmen“; dort immer samt Ehemaligen. `mitKuerzel` stellt das
 * Unternehmenskürzel davor, wo kein Kürzel-Chip daneben steht.
 */
export function PartnerRoleBadges({ relation, mitKuerzel }: { relation: Zuordnung; mitKuerzel: boolean }): ReactElement {
  return (
    <>
      {mitKuerzel && <Chip>{relation.shortCode}</Chip>}
      {partnerBadges(relation, true).map((text) => <Chip key={text}>{text}</Chip>)}
    </>
  );
}
